<script setup>
// @ts-check

import * as _ from 'lodash-es'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps({
  toast: {
    /** @type import('vue').PropType<import('@/core/composables/useToasts').ToastWithId> */
    type: Object,
    required: true,
    /** @param {any} toast */
    validator: (toast) => ['info', 'success', 'danger'].includes(toast.type)
  }
})

const emit = defineEmits(['close'])

/**
 * Keeps track of progress of toast' progress bar.
 */
const progress = ref(100)

/**
 * Keeps track of the timer used to automatically closed a timed toast.
 * @type {import('vue').Ref<?NodeJS.Timer>}
 */
const closeTimer = ref(null)

const icon = computed(() =>
  _.get(
    {
      info: '$information-outline',
      success: '$check-circle-outline',
      danger: '$alert-circle-outline'
    },
    props.toast.type
  )
)

// Get progress bar transition duration from toast duration which is in milliseconds
const transitionDuration = computed(() => {
  if (props.toast.duration) {
    const animationDuration = props.toast.duration / 1000
    return animationDuration.toString()
  }

  return '5'
})

onMounted(() => {
  requestAnimationFrame(() => {
    progress.value = 0
  })

  if (props.toast.duration) {
    // Setup automatic close timer.
    closeTimer.value = setTimeout(
      () => emit('close', props.toast.id),
      props.toast.duration
    )
  }
})

onUnmounted(() => {
  // Cleanup automatic close timer.
  if (closeTimer.value) {
    clearTimeout(closeTimer.value)
  }
})
</script>

<template>
  <div
    role="alert"
    class="relative rounded-sm border p-3 shadow-sm"
    :class="`toast-${toast.type}`"
  >
    <div class="mr-7 flex flex-row items-start justify-between gap-x-4">
      <VIcon :icon size="1.5rem" class="toast-icon" />
      <p class="grow">
        {{ toast.content }}
      </p>
    </div>
    <button
      :aria-label="$t('general.close')"
      type="button"
      class="absolute top-2 right-2 flex items-center justify-center rounded-full p-2 hover:bg-white"
      @click.stop="emit('close', toast.id)"
    >
      <VIcon icon="$close" size="1rem" class="text-black" />
    </button>
    <div
      v-if="toast.duration && progress >= 0"
      class="toast-progress-bg w-full rounded-full"
    >
      <div
        class="toast-progress mt-2 h-2 w-full animate-pulse rounded-full"
        :style="`transition: width ${transitionDuration}s ease-in-out; width: ${progress}%`"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.toast-info {
  @apply bg-info-light border-info;

  .toast-icon {
    @apply text-info;
  }

  .toast-progress-bg {
    @apply bg-info-100/65;
  }

  .toast-progress {
    @apply bg-info;
  }
}

.toast-danger {
  @apply bg-danger-light border-danger;

  .toast-icon {
    @apply text-danger;
  }

  .toast-progress-bg {
    @apply bg-danger-100/65;
  }

  .toast-progress {
    @apply bg-danger;
  }
}

.toast-success {
  @apply bg-success-light border-success;

  .toast-icon {
    @apply text-success;
  }

  .toast-progress-bg {
    @apply bg-success-100/65;
  }

  .toast-progress {
    @apply bg-success;
  }
}
</style>
